import { FC, useState } from 'react'
import { ReactComponent as ScalesIcon } from 'src/assets/svg/ri-scales-3-fill.svg'
import Button from 'src/components/Button'
import { Skeleton } from 'src/components/Skeleton'
import { MentionsTab } from './MentionsTab'
import { SentimentsTab } from './SentimentsTab'
import { SourcesTab } from './SourcesTab'
import styles from './campaignComparisonChart.module.scss'

interface SelectedCampaign {
  id: string
  name: string
}

interface CampaignComparisonChartProps {
  benchmarkCampaign: string | null
  comparisonData?: any
  isLoading?: boolean
  onChangeComparison: () => void
  onClearComparison?: () => void
  selectedCampaigns: SelectedCampaign[]
}

type TabType = 'mentions' | 'sources' | 'sentiments'

// All dimensions below are the ones the removed `.skeleton*` rules in
// campaignComparisonChart.module.scss used, so the placeholder keeps the exact
// footprint it had before the migration to the shared <Skeleton> primitive.
const STAT_ICON_SIZE = 40
const STAT_ICON_RADIUS = 8
const STAT_LABEL_HEIGHT = 14
const STAT_LABEL_WIDTH = 80
const STAT_VALUE_HEIGHT = 20
const STAT_VALUE_WIDTH = 100
const BAR_HEIGHT = 120
const BAR_WIDTH = 40
const BAR_RADIUS = '4px 4px 0 0'
const X_LABEL_HEIGHT = 14
const X_LABEL_WIDTH = 60
const Y_LABEL_HEIGHT = 12
const Y_LABEL_WIDTH = 20
const STAT_CARD_COUNT = 3
const Y_LABEL_COUNT = 5
// Mirrors the real bar count when we already know it, so the chart does not
// re-space itself when the data lands.
const FALLBACK_BAR_COUNT = 5
const MAX_CAMPAIGNS = 9

export const CampaignComparisonChart: FC<CampaignComparisonChartProps> = ({
  selectedCampaigns,
  benchmarkCampaign,
  comparisonData,
  isLoading = false,
  onChangeComparison,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('mentions')

  // Use API data if available, otherwise empty array
  const selectedData = comparisonData?.campaigns || []

  // Skeletons are for the FIRST load only. A refetch (e.g. a date-range change)
  // keeps the existing bars and stats on screen instead of flashing the whole
  // chart back to placeholders for data that is already there.
  const isInitialLoading = isLoading && !comparisonData

  const skeletonBarCount =
    selectedCampaigns.length > 0 && selectedCampaigns.length <= MAX_CAMPAIGNS
      ? selectedCampaigns.length
      : FALLBACK_BAR_COUNT

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2 className={styles.title}>Campaigns Comparison</h2>
        </div>
        <Button background="malibu" onClick={onChangeComparison}>
          <ScalesIcon className={styles.buttonIcon} />
          Change Comparison
        </Button>
      </div>

      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tab} ${activeTab === 'mentions' ? styles.active : ''}`}
          onClick={() => setActiveTab('mentions')}
        >
          Campaigns
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sources' ? styles.active : ''}`}
          onClick={() => setActiveTab('sources')}
        >
          Sources
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sentiments' ? styles.active : ''}`}
          onClick={() => setActiveTab('sentiments')}
        >
          Sentiments
        </button>
      </div>

      {isInitialLoading ? (
        <div className={styles.loadingContainer}>
          {/* Stats Cards Skeleton */}
          <div className={styles.statsRow}>
            {Array.from({ length: STAT_CARD_COUNT }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.statCard} key={index}>
                <div className={styles.statIcon}>
                  <Skeleton
                    height={STAT_ICON_SIZE}
                    radius={STAT_ICON_RADIUS}
                    width={STAT_ICON_SIZE}
                  />
                </div>
                <div className={styles.statContent}>
                  <Skeleton
                    height={STAT_LABEL_HEIGHT}
                    width={STAT_LABEL_WIDTH}
                  />
                  <Skeleton
                    height={STAT_VALUE_HEIGHT}
                    width={STAT_VALUE_WIDTH}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Chart Skeleton */}
          <div className={styles.chartContainer}>
            <div className={styles.chart}>
              <div className={styles.yAxis}>
                {Array.from({ length: Y_LABEL_COUNT }).map((_, index) => (
                  <Skeleton
                    height={Y_LABEL_HEIGHT}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    width={Y_LABEL_WIDTH}
                  />
                ))}
              </div>
              <div className={styles.chartArea}>
                <div className={styles.gridLines}>
                  {Array.from({ length: Y_LABEL_COUNT }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={styles.gridLine} key={index} />
                  ))}
                </div>
                <div className={styles.bars}>
                  {Array.from({ length: skeletonBarCount }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={styles.barWrapper} key={index}>
                      <Skeleton
                        height={BAR_HEIGHT}
                        radius={BAR_RADIUS}
                        width={BAR_WIDTH}
                      />
                    </div>
                  ))}
                </div>
                <div className={styles.xAxis}>
                  {Array.from({ length: skeletonBarCount }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={styles.xLabel} key={index}>
                      <Skeleton height={X_LABEL_HEIGHT} width={X_LABEL_WIDTH} />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.yAxisRight}>
                {Array.from({ length: Y_LABEL_COUNT }).map((_, index) => (
                  <Skeleton
                    height={Y_LABEL_HEIGHT}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    width={Y_LABEL_WIDTH}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'mentions' && (
            <MentionsTab
              benchmarkCampaign={benchmarkCampaign}
              selectedData={selectedData}
              stats={comparisonData?.stats}
            />
          )}
          {activeTab === 'sources' && (
            <SourcesTab
              benchmarkCampaign={benchmarkCampaign}
              selectedData={selectedData}
              stats={comparisonData?.stats}
            />
          )}
          {activeTab === 'sentiments' && (
            <SentimentsTab
              benchmarkCampaign={benchmarkCampaign}
              selectedData={selectedData}
              stats={comparisonData?.stats}
            />
          )}
        </>
      )}
    </div>
  )
}
